
import { map, debounceTime } from 'rxjs/operators'
import { Observable, Subscriber, merge, fromEvent } from 'rxjs'
import { combineEpics } from 'redux-observable'
import * as reduce from 'lodash/reduce'
import { Action } from 'actions'
import {
  setMediaMatchActionCreator,
  setMediaMatchesActionCreator,
  setViewportMediaMatchActionCreator,
  setFullscreenModeActionCreator,
} from 'actions/viewportActions'
import { MEDIA_CONFIG, getFullscreenElement } from 'helpers/viewportHelpers'
import { MediaMatches, MediaMatchName, ViewportDefinition, ViewportMatchName } from 'interfaces/viewport'
import { Epic } from 'interfaces/middleware'
import { Window, MediaQueryListEvent } from 'interfaces/DOM'
import { FULLSCREEN_CHANGE_EVENTS } from 'constants/events'

declare const window: Window

const definitions = Array.from(MEDIA_CONFIG.viewportMap.values())

const buildMediaByDefinition = (def: ViewportDefinition) =>
  `${ def.lowerPart }${ def.upperPart ? ` and ${ def.upperPart }` : '' }`

const getCurrentViewportMatch = (): ViewportMatchName => {
  const mediaMatch = definitions.find((def) => {
    const mediaQuery = buildMediaByDefinition(def)
    return window.matchMedia(mediaQuery).matches
  })
  return mediaMatch ? mediaMatch.name : 'not-defined'
}

const viewportMatchesObservable: Observable<Action> = Observable.create((observer: Subscriber<Action>) => {
  const emitViewportMatch = (breakpoint: ViewportMatchName) => {
    return observer.next(setViewportMediaMatchActionCreator(breakpoint))
  }

  definitions.forEach((def) => {
    const handleViewportChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        emitViewportMatch(def.name)
      }
    }
    const mediaQuery = buildMediaByDefinition(def)
    window.matchMedia(mediaQuery).addListener(handleViewportChange)
  })

  const currentViewport = getCurrentViewportMatch()
  emitViewportMatch(currentViewport)
})

const viewportMatchesEpic: Epic = () =>
  viewportMatchesObservable.pipe(
    // avoid dispatching of the intermediate breakpoint action when maximizing/minimizing browser window
    debounceTime(30))

const mediaMatchesObservable: Observable<Action> = Observable.create((observer: Subscriber<Action>) => {
  const processMediaMatchChange = (name: MediaMatchName) => {
    const matchValue = window.matchMedia(MEDIA_CONFIG.mediaQueries[name]).matches
    observer.next(setMediaMatchActionCreator(name, matchValue))
  }

  const initialMediaMatches: MediaMatches = reduce<string, MediaMatches>(
    MEDIA_CONFIG.mediaQueries,
    (result, value, key) => {
      const matchResult = window.matchMedia(value)
      matchResult.addListener(() => processMediaMatchChange(key))

      return { ...result, [key]: matchResult.matches }
    },
    {}
  )

  observer.next(setMediaMatchesActionCreator(initialMediaMatches))
})

const mediaMatchesEpic: Epic = () => mediaMatchesObservable

const fullscreenChange$ = merge(...FULLSCREEN_CHANGE_EVENTS.map((eventName) => fromEvent(document, eventName)))
const fullscreenModeEpic: Epic = () => {
  return fullscreenChange$.pipe(map(() => setFullscreenModeActionCreator(Boolean(getFullscreenElement()))))
}

export default combineEpics(
  fullscreenModeEpic,
  viewportMatchesEpic,
  mediaMatchesEpic
)
